import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useApiHandler } from './hooks/useApiHandler';
import { TicketTable } from './TicketTable/TicketTable';
import GIButton from '../../../components/common/button/GIButton';
import { useModalDisclosures } from './hooks/useModalDisclosures';
import { CancelTicketModal } from './components/CancelTicketModal';
import { ReopenTicketModal } from './components/ReopenTicketModal';
import { ExportTicketsModal } from './components/ExportTicketsModal';
import { CompleteTicketModal } from './components/CompleteTicketModal';
import { ReassignTicketModal } from './components/ReassignTicketModal';
import { MarkVerifyCompleteModal } from './components/MarkVerifyCompleteModal';
import { CancelTicketReasonModal } from './components/CancelTicketReasonModal';
import { BulkReassignTicketModal } from './components/BulkReassignTicketModal';
import { StyledDownloadButton, StyledRecordsNotFound, StyledHeaderButtonGroup } from './constants/ticketlist.styled';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';

export const ResultsSection = (props: any) => {
    const {
        tickets,
        searchCriteria,
        ticketsCount,
        pageSortInfo,
        setPageSortInfo,
        isAdvanceSearch,
        viewTicketPermission,
    } = props;
    const dispatch = useDispatch();
    const intl = useIntl();

    const { bulkReassignmentUsersAPI } = useApiHandler();

    const {
        isCancelTicketOpen,
        isCancelTicketReasonOpen,
        onCancelTicketClose,
        onCancelTicketReasonClose,
        isCompleteVerifyTicketOpen,
        onCompleteVerifyTicketClose,
        isCompleteTicketOpen,
        onCompleteTicketClose,
        onCancelTicketReasonOpen,
        isReassignTicketOpen,
        onReassignTicketOpen,
        onReassignTicketClose,
        isReopenTicketOpen,
        onReopenTicketClose,
        onCompleteTicketOpen,
        onCompleteVerifyTicketOpen,
        onCancelTicketOpen,
        onReopenTicketOpen,
        isBulkReassignTicketOpen,
        onBulkReassignTicketOpen,
        onBulkReassignTicketClose,
        isExportTicketsOpen,
        onExportTicketsOpen,
        onExportTicketsClose,
    } = useModalDisclosures();

    const [selectedRows, setSelectedRows] = useState([]);

    const onRowSelect = useCallback((selectedRows: any) => {
        setSelectedRows(selectedRows.map((row: any) => row.id));
    }, []);

    const handleBulkReassign = async () => {
        onBulkReassignTicketOpen();
        await bulkReassignmentUsersAPI(dispatch, {});
    };
    return (
        <>
            <ConditionalRendering isTrue={!!tickets.length}>
                <ConditionalRendering isTrue={viewTicketPermission}>
                    <StyledHeaderButtonGroup>
                        <StyledDownloadButton>
                            <GIButton
                                id="downloadTicketsButton"
                                labelId=""
                                icon={<FontAwesomeIcon icon={faDownload} />}
                                isDisabled={false}
                                ariaLabel="Download Tickets"
                                variant="secondary"
                                onClickHandler={() => onExportTicketsOpen()}
                            />
                        </StyledDownloadButton>
                        <ConditionalRendering isTrue={!!selectedRows.length}>
                            <GIButton
                                id="reassignTicketsButton"
                                labelId="ticketList.btn.reassign"
                                onClickHandler={handleBulkReassign}
                                isDisabled={false}
                            />
                        </ConditionalRendering>
                    </StyledHeaderButtonGroup>
                </ConditionalRendering>
                <TicketTable
                    tickets={tickets}
                    searchCriteria={searchCriteria}
                    ticketsCount={ticketsCount}
                    pageSortInfo={pageSortInfo}
                    setPageSortInfo={setPageSortInfo}
                    isAdvanceSearch={isAdvanceSearch}
                    onRowSelect={onRowSelect}
                    onCompleteTicketOpen={onCompleteTicketOpen}
                    onCompleteVerifyTicketOpen={onCompleteVerifyTicketOpen}
                    onReassignTicketOpen={onReassignTicketOpen}
                    onCancelTicketOpen={onCancelTicketOpen}
                    onReopenTicketOpen={onReopenTicketOpen}
                    viewTicketPermission={viewTicketPermission}
                />
            </ConditionalRendering>
            <ConditionalRendering isTrue={!tickets.length}>
                <StyledRecordsNotFound data-testid="no-tickets-found">
                    {intl.formatMessage({ id: 'ticketList.noDataFound' })}
                </StyledRecordsNotFound>
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCancelTicketOpen}>
                <CancelTicketModal onClose={onCancelTicketClose} onCancelTicketReasonOpen={onCancelTicketReasonOpen} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCancelTicketReasonOpen}>
                <CancelTicketReasonModal onClose={onCancelTicketReasonClose} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCompleteTicketOpen}>
                <CompleteTicketModal onClose={onCompleteTicketClose} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCompleteVerifyTicketOpen}>
                <MarkVerifyCompleteModal onClose={onCompleteVerifyTicketClose} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isReassignTicketOpen}>
                <ReassignTicketModal onClose={onReassignTicketClose} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isReopenTicketOpen}>
                <ReopenTicketModal onClose={onReopenTicketClose} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isBulkReassignTicketOpen}>
                <BulkReassignTicketModal onClose={onBulkReassignTicketClose} selectedRows={selectedRows} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isExportTicketsOpen}>
                <ExportTicketsModal onClose={onExportTicketsClose} />
            </ConditionalRendering>
        </>
    );
};
